import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError, API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Image as ImageIcon, MapPin, Trash, Eye, DownloadSimple, X, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Fotos() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [grouped, setGrouped] = useState({});
  const [clientes, setClientes] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [selected, setSelected] = useState(new Set()); // ids selected for download

  const [form, setForm] = useState({
    cliente_id: "",
    endereco: "",
    data: new Date().toISOString().split("T")[0],
    descricao: "",
    image_base64: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [f, c] = await Promise.all([api.get("/fotos"), api.get("/clientes")]);
      setClientes(c.data);
      // Group by cliente_id + endereco
      const g = {};
      f.data.forEach((foto) => {
        const cli = c.data.find((x) => x.id === foto.cliente_id);
        const key = `${foto.cliente_id}|${foto.endereco}`;
        if (!g[key]) {
          g[key] = {
            cliente_id: foto.cliente_id,
            cliente_nome: cli?.nome || "—",
            endereco: foto.endereco,
            fotos: [],
          };
        }
        g[key].fotos.push(foto);
      });
      setGrouped(g);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onClienteChange = (cid) => {
    const cli = clientes.find((c) => c.id === cid);
    setForm({ ...form, cliente_id: cid, endereco: cli?.endereco || "" });
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm({ ...form, image_base64: ev.target.result });
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image_base64) {
      toast.error(t("common.error"));
      return;
    }
    try {
      await api.post("/fotos", form);
      toast.success(t("common.success"));
      setOpen(false);
      setForm({ ...form, image_base64: "", descricao: "" });
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const viewFoto = async (id) => {
    try {
      const res = await api.get(`/fotos/${id}`);
      setViewing(res.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("horas.confirmDelete"))) return;
    await api.delete(`/fotos/${id}`);
    toast.success(t("common.success"));
    load();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectGroup = (fotos) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = fotos.every((f) => next.has(f.id));
      if (allSelected) fotos.forEach((f) => next.delete(f.id));
      else fotos.forEach((f) => next.add(f.id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const downloadSelected = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const loadingId = toast.loading(`${t("fotos.downloading")} (${ids.length})...`);
    try {
      const token = localStorage.getItem("modolo_token");
      const res = await fetch(`${API}/fotos/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const filename = `fotos_modolo_${ts}.zip`;
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 1500);
      toast.success(`${filename} (${ids.length} ${t("fotos.photosCount")})`, { id: loadingId });
      clearSelection();
    } catch (err) {
      toast.error(`${t("common.error")}: ${err.message}`, { id: loadingId });
    }
  };

  const downloadSingle = async (foto) => {
    try {
      // foto.image_base64 is already loaded when user clicked "view"
      // but for simplicity fetch fresh
      const res = await api.get(`/fotos/${foto.id}`);
      const dataUrl = res.data.image_base64;
      if (!dataUrl) throw new Error("no data");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${(foto.endereco || "foto").replace(/[^a-zA-Z0-9_-]/g, "_")}_${foto.data}_${foto.id.slice(0, 6)}.jpg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("common.success"));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="fotos-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("fotos.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("fotos.subtitle")}</p>
        </div>

        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <Button
                variant="outline"
                onClick={clearSelection}
                className="gap-1"
                data-testid="clear-selection-btn"
              >
                <X size={16} /> {selected.size}
              </Button>
              <Button
                onClick={downloadSelected}
                className="bg-red-600 hover:bg-red-700 text-white gap-2"
                data-testid="download-selected-btn"
              >
                <DownloadSimple size={18} weight="bold" />
                {t("fotos.downloadZip")} ({selected.size})
              </Button>
            </>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 text-white gap-2" data-testid="add-foto-btn">
              <Plus size={18} weight="bold" /> {t("fotos.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("fotos.add")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t("fotos.client")}</Label>
                <Select value={form.cliente_id} onValueChange={onClienteChange}>
                  <SelectTrigger data-testid="foto-client-select"><SelectValue placeholder={t("fotos.selectClient")} /></SelectTrigger>
                  <SelectContent>
                    {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("fotos.address")}</Label>
                <Input value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} required data-testid="foto-address-input" />
              </div>
              <div>
                <Label>{t("fotos.date")}</Label>
                <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} required />
              </div>
              <div>
                <Label>{t("fotos.file")}</Label>
                <Input type="file" accept="image/*" onChange={handleFile} required data-testid="foto-file-input" />
              </div>
              <div>
                <Label>{t("fotos.description")}</Label>
                <Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} rows={2} />
              </div>
              {form.image_base64 && (
                <img src={form.image_base64} alt="preview" className="w-full max-h-48 object-cover rounded-lg border border-slate-200" />
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("fotos.cancel")}</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="foto-save-btn">{t("fotos.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : Object.keys(grouped).length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <ImageIcon size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("fotos.empty")}
        </Card>
      ) : (
        <Accordion type="multiple" className="space-y-3">
          {Object.entries(grouped).map(([key, g]) => {
            const allSelected = g.fotos.length > 0 && g.fotos.every((f) => selected.has(f.id));
            const someSelected = g.fotos.some((f) => selected.has(f.id));
            return (
              <AccordionItem key={key} value={key} className="border border-slate-200 rounded-xl bg-white px-4" data-testid={`foto-group-${key}`}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3 flex-1 text-left">
                    <div
                      role="checkbox"
                      aria-checked={allSelected}
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); toggleSelectGroup(g.fotos); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); toggleSelectGroup(g.fotos); } }}
                      className={`cursor-pointer flex items-center justify-center w-5 h-5 rounded border ${allSelected ? "bg-green-600 border-green-600 text-white" : someSelected ? "bg-slate-300 border-slate-400" : "bg-white border-slate-300"}`}
                      data-testid={`select-group-${key}`}
                    >
                      {allSelected && <Check size={12} weight="bold" />}
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-green-50 text-green-700 flex items-center justify-center">
                      <MapPin size={20} weight="duotone" />
                    </div>
                    <div className="flex-1">
                      <div className="heading-font font-semibold text-slate-900">{g.cliente_nome}</div>
                      <div className="text-xs text-slate-500">{g.endereco}</div>
                    </div>
                    <Badge className="bg-slate-100 text-slate-700 border-0 mr-3">
                      {g.fotos.length} {t("fotos.photosCount")}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <LazyPhotoGrid
                    fotos={g.fotos}
                    onView={viewFoto}
                    onDelete={handleDelete}
                    onDownload={downloadSingle}
                    onToggleSelect={toggleSelect}
                    selected={selected}
                    isAdmin={isAdmin}
                  />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{viewing?.descricao || t("fotos.view")}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div>
              <img src={viewing.image_base64} alt="foto" className="w-full rounded-lg" />
              <div className="text-xs text-slate-500 mt-2">{viewing.data} — {viewing.endereco}</div>
            </div>
          )}
          <DialogFooter>
            {viewing && (
              <Button onClick={() => downloadSingle(viewing)} className="gap-2 bg-red-600 hover:bg-red-700">
                <DownloadSimple size={16} weight="bold" /> {t("fotos.download")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LazyPhotoThumb({ id }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get(`/fotos/${id}`)
      .then((r) => { if (!cancelled) setSrc(r.data.image_base64); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [id]);
  if (error) {
    return <ImageIcon size={30} weight="duotone" className="text-slate-300" />;
  }
  if (!src) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-slate-100 animate-pulse">
        <ImageIcon size={28} weight="duotone" className="text-slate-300" />
      </div>
    );
  }
  return <img src={src} alt="foto" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />;
}

function LazyPhotoGrid({ fotos, onView, onDelete, onDownload, onToggleSelect, selected, isAdmin }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pb-3">
      {fotos.map((f) => {
        const isSel = selected.has(f.id);
        return (
          <div
            key={f.id}
            className={`group relative border rounded-lg overflow-hidden bg-slate-50 aspect-square cursor-pointer card-hover transition-all ${
              isSel ? "border-green-500 ring-2 ring-green-500" : "border-slate-200"
            }`}
            onClick={() => onToggleSelect(f.id)}
            data-testid={`foto-thumb-${f.id}`}
          >
            <LazyPhotoThumb id={f.id} />
            <div className="absolute top-2 left-2 z-10">
              <Checkbox checked={isSel} className="bg-white shadow" />
            </div>
            <div className="absolute bottom-2 left-2 z-10 text-[10px] text-white font-semibold bg-black/60 rounded px-1.5 py-0.5">{f.data}</div>
            <div className="absolute inset-x-0 bottom-0 z-20 flex items-center justify-center gap-1 p-1 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                variant="secondary"
                className="h-7 px-2"
                onClick={(e) => { e.stopPropagation(); onView(f.id); }}
                data-testid={`view-foto-${f.id}`}
              >
                <Eye size={13} />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="h-7 px-2"
                onClick={(e) => { e.stopPropagation(); onDownload(f); }}
                data-testid={`download-foto-${f.id}`}
              >
                <DownloadSimple size={13} />
              </Button>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-7 px-2"
                  onClick={(e) => { e.stopPropagation(); onDelete(f.id); }}
                  data-testid={`delete-foto-${f.id}`}
                >
                  <Trash size={13} />
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
